import { useState } from 'react'
import { Plus } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Spinner } from '../../lib/shadcn/spinner'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { toast } from '../../lib/shadcn/sonner'
import { useRecordMaintenanceLine } from '../../hooks/backend/fleet'
import { writeErrorMessage } from '../../utils/writeError'
import type { FleetRow } from '../../utils/fleet'
import { MAINTENANCE_CATEGORIES, monthsAgo, zarExact } from '../../utils/servicing'

/**
 * Record one servicing or repair line.
 *
 * The amount is captured EXCLUDING VAT with VAT beside it, matching how the
 * supplier invoice reads and how the workbook this replaces was built. It also
 * matters for correctness: the workbook computes VAT as amount times 0.15, so
 * every category figure in it is VAT-exclusive, and five of its ten months then
 * subtracted that VAT a second time from a base that never contained it. Holding
 * the two apart is what makes that class of error impossible here.
 *
 * VAT defaults to 15 percent of the amount and can be set to zero, because some
 * suppliers are not VAT registered and forcing VAT on would overstate what the
 * owner is charged.
 */
export function RecordMaintenanceDialog({
  vehicles, defaultVehicleId, defaultPeriod, onSaved,
}: {
  vehicles: FleetRow[]
  defaultVehicleId?: string
  defaultPeriod?: string
  onSaved: () => void
}) {
  const [open, setOpen] = useState(false)
  const [vehicleId, setVehicleId] = useState(defaultVehicleId ?? '')
  const [period, setPeriod] = useState((defaultPeriod ?? monthsAgo(0)).slice(0, 7))
  const [category, setCategory] = useState('service')
  const [amount, setAmount] = useState('')
  const [vatMode, setVatMode] = useState<'standard' | 'zero' | 'custom'>('standard')
  const [customVat, setCustomVat] = useState('')
  const [description, setDescription] = useState('')
  const [incurredOn, setIncurredOn] = useState('')
  const [odometer, setOdometer] = useState('')
  const record = useRecordMaintenanceLine()

  const amountNum = amount === '' ? null : Number(amount)
  const vatNum =
    vatMode === 'zero' ? 0
    : vatMode === 'custom' ? (customVat === '' ? null : Number(customVat))
    : amountNum === null ? null : Math.round(amountNum * 0.15 * 100) / 100
  const total = amountNum === null || vatNum === null ? null : amountNum + vatNum

  function reset() {
    setAmount(''); setDescription(''); setIncurredOn(''); setOdometer('')
    setVatMode('standard'); setCustomVat('')
  }

  async function save() {
    if (!vehicleId) { toast.error('Choose a vehicle'); return }
    if (amountNum === null || !Number.isFinite(amountNum)) {
      toast.error('Enter the amount excluding VAT')
      return
    }
    try {
      await record.trigger({
        vehicle_id: vehicleId,
        period_start: `${period}-01`,
        category,
        amount_ex_vat_zar: amountNum,
        // Only 'standard' omits VAT and lets the database apply 15 percent.
        vat_zar: vatMode === 'standard' ? null : vatNum,
        description: description.trim() || null,
        incurred_on: incurredOn || null,
        odometer_km: odometer === '' ? null : Number(odometer),
      }).result
      toast.success('Maintenance line recorded')
      reset()
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not record the line', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (next) reset(); setOpen(next) }}>
      <DialogTrigger asChild>
        <Button size="sm" className="h-8 gap-1.5 px-2.5 text-xs">
          <Plus className="h-3.5 w-3.5" />
          Record spend
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record servicing or repair spend</DialogTitle>
          <DialogDescription>
            Enter the amount as it appears on the invoice, excluding VAT.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="rm-vehicle">Vehicle</Label>
            <NativeSelect id="rm-vehicle" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              <option value="">Choose a vehicle</option>
              {vehicles.map((v) => (
                <option key={v.vehicle_id} value={v.vehicle_id}>
                  {v.registration_number} {[v.make, v.model].filter(Boolean).join(' ')}
                </option>
              ))}
            </NativeSelect>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rm-period">Accounting month</Label>
              <Input id="rm-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rm-category">Category</Label>
              <NativeSelect id="rm-category" value={category} onChange={(e) => setCategory(e.target.value)}>
                {MAINTENANCE_CATEGORIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </NativeSelect>
            </div>
          </div>

          {category === 'fleetly_cost' && (
            <p className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
              A Fleetly cost sits on the vehicle's record but is never charged to the owner under any
              rule the workbook has used.
            </p>
          )}
          {category === 'service' && (
            <p className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
              Service is excluded from the owner charge. That is the one thing all ten months of the
              workbook agreed on.
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rm-amount">Amount excluding VAT (R)</Label>
              <Input id="rm-amount" type="number" inputMode="decimal" value={amount}
                onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rm-vat">VAT</Label>
              <NativeSelect id="rm-vat" value={vatMode}
                onChange={(e) => setVatMode(e.target.value as 'standard' | 'zero' | 'custom')}>
                <option value="standard">Standard 15 percent</option>
                <option value="zero">No VAT, supplier not registered</option>
                <option value="custom">Enter the VAT from the invoice</option>
              </NativeSelect>
            </div>
          </div>

          {vatMode === 'custom' && (
            <div className="space-y-1.5">
              <Label htmlFor="rm-customvat">VAT on the invoice (R)</Label>
              <Input id="rm-customvat" type="number" inputMode="decimal" value={customVat}
                onChange={(e) => setCustomVat(e.target.value)} />
            </div>
          )}

          <div className="rounded-md border border-border bg-background p-3 text-xs">
            <div className="flex justify-between"><span className="text-muted-foreground">Excluding VAT</span>
              <span className="font-medium tabular-nums text-foreground">{zarExact(amountNum)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">VAT</span>
              <span className="font-medium tabular-nums text-foreground">{zarExact(vatNum)}</span></div>
            <div className="mt-1 flex justify-between border-t border-border pt-1">
              <span className="font-semibold text-foreground">Invoice total</span>
              <span className="font-semibold tabular-nums text-foreground">{zarExact(total)}</span></div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rm-desc">What was done</Label>
            <Input id="rm-desc" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Two front tyres, Bridgestone 185/65 R15" />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rm-date">Invoice date</Label>
              <Input id="rm-date" type="date" value={incurredOn} onChange={(e) => setIncurredOn(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rm-odo">Odometer (km)</Label>
              <Input id="rm-odo" type="number" inputMode="numeric" value={odometer}
                onChange={(e) => setOdometer(e.target.value)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={record.loading}>Cancel</Button>
          <Button onClick={save} disabled={record.loading}>
            {record.loading && <Spinner className="mr-2 h-4 w-4" />}
            Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
